import type { BalanceSheetAccountRow, BalanceSheetGroup, BalanceSheetReport } from '@/services/accounting-service';
import type { TreeReport, TreeReportDefinition, TreeRow } from '@/types/report-definition';
import { groupByGlParentCode } from '@/utils/gl-hierarchy';

export type BalanceSheetTreeReportDefinition = TreeReportDefinition<BalanceSheetReport>;

// #460 — a GL linked to a bank account displays the bank's own name (the one
// users actually edit); the GL name is only the fallback, since it goes stale
// on renames and can even come from an archived code.
const displayName = (a: BalanceSheetAccountRow): string => a.bankAccountName ?? a.name;

// GL code renders INLINE with the name ("code · name") at the row's indent —
// matching the P&L / GL-report convention — instead of a separate fixed left
// column. Drill-down stays keyed on glCodeId (the whole row is pressable), and
// both the on-screen tree and the print RowView render `code` conditionally, so
// dropping it collapses the empty column on both surfaces.
const accountRow = (a: BalanceSheetAccountRow, indent: number): TreeRow => ({
	id: a.glCodeId,
	glCodeId: a.glCodeId,
	label: a.code ? `${a.code} · ${displayName(a)}` : displayName(a),
	amount: a.balanceCents,
	indent,
});

const headingRow = (label: string, indent: number): TreeRow => ({
	label,
	amount: 0,
	indent,
	heading: true,
});

const subtotalRow = (label: string, amount: number, indent: number): TreeRow => ({
	label,
	amount,
	indent,
	tone: 'subtotal',
});

/**
 * Push a "Group → rows + Total <Group>" block. Headings only render when the
 * group actually has rows so empty sections collapse instead of leaving a
 * lonely header line.
 *
 * Within the group, dotted sub-accounts (1030.05) nest under their pre-dot
 * parent (1030), P&L-style (#461): the parent is a bold amountless HEADING at
 * `depth + 1`, a parent-direct leaf appears at `depth + 2` only when the parent
 * has its own nonzero balance (split-bank parents are non-postable, so it's
 * usually $0 and would just be noise), children sit at `depth + 2`, and a
 * "Total <parent>" roll-up (tone: subtotal) closes each family. The existing
 * per-group `Total <heading>` still closes the whole block.
 */
function pushGroup(rows: TreeRow[], heading: string, group: BalanceSheetGroup, depth: number): void {
	if (group.rows.length === 0) return;
	rows.push(headingRow(heading, depth));
	for (const { parentCode, parent, children } of groupByGlParentCode(group.rows, (r) => r.code)) {
		// Solo/flat account with no dotted children — emit the single row; a
		// family roll-up would just duplicate it.
		if (children.length === 0 && parent) {
			rows.push(accountRow(parent, depth + 1));
			continue;
		}
		// Frozen month-close snapshots predate the server's parent-row synthesis,
		// so `parent` can still be null there — fall back to the bare code.
		const familyName = parent ? `${parentCode} · ${displayName(parent)}` : parentCode;
		rows.push({ ...headingRow(familyName, depth + 1), id: `head-${parentCode}` });
		if (parent && parent.balanceCents !== 0) rows.push(accountRow(parent, depth + 2));
		for (const a of children) rows.push(accountRow(a, depth + 2));
		const familyTotal = (parent?.balanceCents ?? 0) + children.reduce((sum, a) => sum + a.balanceCents, 0);
		rows.push(subtotalRow(`Total ${familyName}`, familyTotal, depth + 1));
	}
	rows.push(subtotalRow(`Total ${heading}`, group.totalCents, depth + 1));
}

/**
 * Balance Sheet — QuickBooks-style hierarchical layout. One section per
 * top-level group (Assets / Liabilities & Equity) with sub-headings
 * (Current Assets → Checking/Savings, Accounts Receivable, etc.) and
 * per-GL-code rows. Net Income appears as a row inside Equity so
 * Total Equity = sum(equity accounts) + Net Income.
 */
export const balanceSheetReport: BalanceSheetTreeReportDefinition = {
	id: 'balance-sheet',
	title: 'Balance Sheet',
	subtitle: 'Assets vs. Liabilities & Equity at a point in time.',
	flavor: 'tree',
	printUrl: (associationId, params) => {
		const sp = new URLSearchParams();
		if (params.asOf) sp.set('asOf', String(params.asOf));
		if (params.basis) sp.set('basis', String(params.basis));
		const qs = sp.toString();
		return `/print/balance-sheet/${associationId}${qs ? `?${qs}` : ''}`;
	},
	buildTree: (data: BalanceSheetReport): TreeReport => {
		const basis = data.params.basis ?? 'accrual';

		const assetRows: TreeRow[] = [];
		assetRows.push(headingRow('Current Assets', 0));
		pushGroup(assetRows, 'Checking/Savings', data.assets.cash, 1);
		if (basis === 'accrual') pushGroup(assetRows, 'Accounts Receivable', data.assets.accountsReceivable, 1);
		pushGroup(assetRows, 'Other Current Assets', data.assets.otherCurrent, 1);
		assetRows.push(subtotalRow('Total Current Assets', data.assets.currentTotalCents, 1));
		if (data.assets.fixed.rows.length > 0) {
			pushGroup(assetRows, 'Fixed Assets', data.assets.fixed, 0);
		}

		const liabEquityRows: TreeRow[] = [];
		const hasAnyLiab = data.liabilities.accountsPayable.rows.length > 0
			|| data.liabilities.otherCurrent.rows.length > 0
			|| data.liabilities.longTerm.rows.length > 0;
		if (hasAnyLiab) {
			liabEquityRows.push(headingRow('Liabilities', 0));
			const hasCurrentLiab = data.liabilities.accountsPayable.rows.length > 0
				|| data.liabilities.otherCurrent.rows.length > 0;
			if (hasCurrentLiab) {
				liabEquityRows.push(headingRow('Current Liabilities', 1));
				if (basis === 'accrual') pushGroup(liabEquityRows, 'Accounts Payable', data.liabilities.accountsPayable, 2);
				pushGroup(liabEquityRows, 'Other Current Liabilities', data.liabilities.otherCurrent, 2);
				liabEquityRows.push(subtotalRow('Total Current Liabilities', data.liabilities.currentTotalCents, 2));
			}
			if (data.liabilities.longTerm.rows.length > 0) {
				pushGroup(liabEquityRows, 'Long-Term Liabilities', data.liabilities.longTerm, 1);
			}
			liabEquityRows.push(subtotalRow('Total Liabilities', data.liabilities.totalCents, 1));
		}

		liabEquityRows.push(headingRow('Equity', 0));
		for (const e of data.equity.rows) liabEquityRows.push(accountRow(e, 1));
		if (data.equity.retainedEarningsCents !== 0) {
			liabEquityRows.push({ label: 'Beginning Fund Balance', amount: data.equity.retainedEarningsCents, indent: 1 });
		}
		liabEquityRows.push({ label: 'Net Income', amount: data.equity.netIncomeCents, indent: 1 });
		liabEquityRows.push(subtotalRow('Total Equity', data.equity.totalCents, 1));

		const tieOutCents = data.assets.totalCents - data.totalLiabilitiesAndEquityCents;
		const noteParts: string[] = [];
		if (basis === 'cash') {
			noteParts.push(
				'Cash basis: accrued A/R and A/P are excluded. Equity reflects cash on hand plus YTD cash-basis Net Income.',
			);
		}
		if (Math.abs(tieOutCents) > 0) {
			const fmt = (n: number): string => `$${(Math.abs(n) / 100).toFixed(2)}`;
			noteParts.push(
				`Tie-out check: Assets − (Liabilities + Equity) = ${tieOutCents > 0 ? '+' : '−'}${fmt(tieOutCents)}. `
				+ 'A non-zero figure means a transaction posted to one side without a matching offset; '
				+ 'inspect the General Ledger around this period to locate the unbalanced entry.',
			);
		} else {
			noteParts.push(
				'Tie-out check: Assets equal Liabilities + Equity to the penny. '
				+ 'The Beginning Fund Balance line carries any prior-period equity (closed RE + opening offsets) that keeps the equation balanced.',
			);
		}

		return {
			columns: [
				{
					sections: [
						{
							id: 'assets',
							title: 'Assets',
							rows: assetRows,
							total: { label: 'TOTAL ASSETS', amount: data.assets.totalCents },
							emptyMessage: 'No asset balances at this date.',
						},
						{
							id: 'liab-equity',
							title: 'Liabilities & Equity',
							rows: liabEquityRows,
							total: { label: 'TOTAL LIABILITIES & EQUITY', amount: data.totalLiabilitiesAndEquityCents },
							emptyMessage: 'No liability or equity balances at this date.',
						},
					],
				},
			],
			note: noteParts.length > 0 ? noteParts.join(' • ') : undefined,
		};
	},
};
